import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportOptionsModal = ({ onClose, onExport, selectedTemplate, reportConfig }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [includeScheduling, setIncludeScheduling] = useState(false);

  const exportFormats = [
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Branded PDF document with charts and insights',
      icon: 'FileText',
      size: '~3.2 MB',
      color: 'primary'
    },
    {
      id: 'pptx',
      name: 'PowerPoint Presentation',
      description: 'Editable slides for stakeholder presentations',
      icon: 'Presentation',
      size: '~4.5 MB',
      color: 'warning'
    },
    {
      id: 'docx',
      name: 'Word Document',
      description: 'Editable document format for collaboration',
      icon: 'FileText',
      size: '~2.8 MB',
      color: 'success'
    },
    {
      id: 'xlsx',
      name: 'Excel Spreadsheet',
      description: 'Data tables and financial projections',
      icon: 'FileSpreadsheet',
      size: '~1.2 MB',
      color: 'accent'
    }
  ];

  const handleExport = () => {
    onExport(selectedFormat);
  };

  const getColorClasses = (color) => {
    const colors = {
      primary: { bg: 'bg-primary/10', icon: 'var(--color-primary)' },
      success: { bg: 'bg-success/10', icon: 'var(--color-success)' },
      warning: { bg: 'bg-warning/10', icon: 'var(--color-warning)' },
      accent: { bg: 'bg-accent/10', icon: 'var(--color-accent)' }
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto elevation-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground font-heading flex items-center gap-2">
              <Icon name="Download" size={24} className="text-primary" />
              Export Report
            </h2>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Choose format and configure export options
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Selected Template Info */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
              <div>
                <p className="text-sm font-semibold text-foreground font-heading">
                  Template: {selectedTemplate?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </p>
                <p className="text-xs text-muted-foreground font-caption">
                  Company: {reportConfig?.branding?.companyName}
                </p>
              </div>
            </div>
          </div>

          {/* Export Format Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-3">
              Select Export Format
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {exportFormats?.map((format) => {
                const colorClasses = getColorClasses(format?.color);
                return (
                  <button
                    key={format?.id}
                    onClick={() => setSelectedFormat(format?.id)}
                    className={`flex items-start gap-3 p-4 border-2 rounded-lg transition-smooth text-left ${
                      selectedFormat === format?.id
                        ? 'border-primary bg-primary/10 elevation-md' :'border-border hover:bg-muted hover:elevation-sm'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-lg ${colorClasses?.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon name={format?.icon} size={20} color={colorClasses?.icon} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-foreground font-heading mb-1">
                        {format?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground font-caption mb-2">
                        {format?.description}
                      </p>
                      <span className="text-xs text-muted-foreground font-caption">
                        Estimated size: {format?.size}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sharing Options */}
          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-3">
              Sharing & Distribution
            </label>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-muted cursor-pointer transition-smooth">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-foreground font-body">Generate Shareable Link</p>
                  <p className="text-xs text-muted-foreground font-caption">Create secure link for stakeholder access</p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-muted cursor-pointer transition-smooth">
                <input
                  type="checkbox"
                  checked={includeScheduling}
                  onChange={(e) => setIncludeScheduling(e?.target?.checked)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-foreground font-body">Email Delivery</p>
                  <p className="text-xs text-muted-foreground font-caption">Send report to stakeholder distribution list</p>
                </div>
              </label>

              {includeScheduling && (
                <div className="ml-7 p-3 bg-muted rounded-lg">
                  <input
                    type="email"
                    placeholder="Enter email addresses (comma-separated)"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Report Scheduling (Coming Soon) */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Clock" size={18} color="var(--color-warning)" className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground font-body mb-1">
                  Automated Report Scheduling
                </p>
                <p className="text-xs text-muted-foreground font-caption">
                  Schedule automatic report generation and delivery - Coming Soon
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="text-xs text-muted-foreground font-caption">
            <Icon name="Lock" size={12} className="inline mr-1" />
            Secure download with encryption
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="default"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="default"
              onClick={handleExport}
              iconName="Download"
              iconPosition="left"
            >
              Export {selectedFormat?.toUpperCase()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportOptionsModal;